import { useLayoutEffect, useRef, useState } from 'react';
import { NoteTabLock, pageLock, type LockHooks, type LockState } from './noteTabLock.js';

// ehr#682: set up at page load, so a reload's held note is kept while the page reopens it.
if (typeof window !== 'undefined') pageLock();

/**
 * ehr#682: one open copy per progress note in this browser (lib/noteTabLock). `noteId` null means
 * no lock (a new note before its first save, or a note this person can't edit anyway). It is the
 * lock's key: a note id, or `family:<sessionId>` / `review:<noteId>` (lockKeyFor).
 */
export function useNoteTabLock(
  noteId: string | null,
  hooks: Omit<LockHooks, 'onState'>,
): { state: LockState; readOnly: boolean; takeOver: () => void; notifySaved: () => void } {
  const [state, setState] = useState<LockState>('editable');
  const lock = useRef<NoteTabLock | null>(null);
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  useLayoutEffect(() => {
    if (!noteId || typeof window === 'undefined') {
      setState('editable');
      return;
    }
    const { env, me, reserve } = pageLock();
    reserve.claim(noteId);
    const l = new NoteTabLock(noteId, me, env, {
      onState: setState,
      onLost: () => hooksRef.current.onLost(),
      onRegained: (r) => hooksRef.current.onRegained(r),
      onRemoteSaved: () => hooksRef.current.onRemoteSaved(),
    });
    lock.current = l;
    setState(l.start());
    return () => {
      l.stop();
      lock.current = null;
    };
  }, [noteId]);
  return {
    state,
    readOnly: state !== 'editable',
    takeOver: () => lock.current?.takeOver(),
    notifySaved: () => lock.current?.notifySaved(),
  };
}
